import * as React from "react"
import type { Account, Category } from "@/types"
import type { CollectionTransaction, TransactionKind } from "../../types"
import { collectionLabels } from "../../utils/constants"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/atoms/Dialog"
import { TransactionForm } from "@/features/transactions/components/TransactionForm"
import { AccountForm } from "../molecules/AccountForm"
import { ConfirmDeleteDialog } from "./ConfirmDeleteDialog"
import { ConfirmDeleteAccountDialog } from "./ConfirmDeleteAccountDialog"
import { useDeleteTransaction } from "@/features/transactions/hooks/useTransactions"
import { useDeleteAccount } from "@/features/accounts/hooks/useAccounts"

interface CarteraDialogsProps {
  accounts: Account[]
  accountsById?: Map<number, Account>
  categoriesById?: Map<number, Category>
  transactionType: TransactionKind
  isTransactionDialogOpen: boolean
  editingTransaction?: CollectionTransaction | null
  deletingTransaction?: CollectionTransaction | null
  isAccountDialogOpen: boolean
  editingAccount?: Account | null
  deletingAccount?: Account | null
  onCloseTransactionDialog: () => void
  onCloseDeleteDialog: () => void
  onCloseAccountDialog: () => void
  onCloseDeleteAccountDialog: () => void
}

export const CarteraDialogs = React.memo(function CarteraDialogs({
  accounts,
  accountsById,
  categoriesById,
  transactionType,
  isTransactionDialogOpen,
  editingTransaction,
  deletingTransaction,
  isAccountDialogOpen,
  editingAccount,
  deletingAccount,
  onCloseTransactionDialog,
  onCloseDeleteDialog,
  onCloseAccountDialog,
  onCloseDeleteAccountDialog,
}: CarteraDialogsProps) {
  const { mutate: deleteTransaction, isPending: isDeleting } = useDeleteTransaction()
  const { mutate: deleteAccount, isPending: isDeletingAccount } = useDeleteAccount()

  const handleDeleteConfirm = () => {
    if (!deletingTransaction) return

    deleteTransaction(
      { id: deletingTransaction.id, actionType: deletingTransaction._type },
      {
        onSuccess: () => {
          onCloseDeleteDialog()
        },
      }
    )
  }

  const handleDeleteAccountConfirm = () => {
    if (!deletingAccount) return

    deleteAccount(deletingAccount.id, {
      onSuccess: () => {
        onCloseDeleteAccountDialog()
      },
    })
  }

  const dialogTitle = editingTransaction
    ? `Editar ${collectionLabels[transactionType]}`
    : collectionLabels[transactionType]

  return (
    <>
      {/* Create / Edit Transaction Dialog */}
      <Dialog
        open={isTransactionDialogOpen}
        onOpenChange={(open) => {
          if (!open) onCloseTransactionDialog()
        }}
      >
        <DialogContent className="cartera-dialog max-h-[90dvh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{dialogTitle}</DialogTitle>
            <DialogDescription>
              {editingTransaction
                ? "Modifica los campos del movimiento y guarda los cambios."
                : "Registra el movimiento sin salir de tu flujo de trabajo."}
            </DialogDescription>
          </DialogHeader>
          <TransactionForm
            key={editingTransaction ? `edit-${editingTransaction.id}` : `new-${transactionType}`}
            accounts={accounts}
            initialTab={transactionType}
            initialTransaction={editingTransaction}
            onSuccess={onCloseTransactionDialog}
            onCancel={onCloseTransactionDialog}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Warning Dialog */}
      <ConfirmDeleteDialog
        isOpen={Boolean(deletingTransaction)}
        transaction={deletingTransaction || null}
        accountsById={accountsById}
        categoriesById={categoriesById}
        isDeleting={isDeleting}
        onConfirm={handleDeleteConfirm}
        onCancel={onCloseDeleteDialog}
      />

      {/* Create / Edit Account Dialog */}
      <Dialog
        open={isAccountDialogOpen}
        onOpenChange={(open) => {
          if (!open) onCloseAccountDialog()
        }}
      >
        <DialogContent className="cartera-dialog sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>{editingAccount ? "Editar cuenta" : "Nueva cuenta"}</DialogTitle>
            <DialogDescription>
              {editingAccount
                ? "Modifica los datos y estado de tu cuenta."
                : "Agrega una cuenta disponible o de ahorro a tu cartera."}
            </DialogDescription>
          </DialogHeader>
          <AccountForm
            key={editingAccount ? `edit-account-${editingAccount.id}` : "new-account"}
            initialAccount={editingAccount}
            onSuccess={onCloseAccountDialog}
            onCancel={onCloseAccountDialog}
          />
        </DialogContent>
      </Dialog>

      {/* Delete / Deactivate Account Confirmation Dialog */}
      <ConfirmDeleteAccountDialog
        isOpen={Boolean(deletingAccount)}
        account={deletingAccount || null}
        isDeleting={isDeletingAccount}
        onConfirm={handleDeleteAccountConfirm}
        onCancel={onCloseDeleteAccountDialog}
      />
    </>
  )
})


